import React from 'react';
import { Progress, Row, Col, Divider, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

const candidate_status_map = {
  FL: 'Fail',
  PS: 'Pass',
  CM: 'To Evaluate',
};

const PracticeTestStats = ({ assessment, recordings }) => {
  const { t } = useTranslation('practice_tests');
  const hasSections = assessment.sections && assessment.sections.length > 0;

  const getSectionScores = () => {
    if (!hasSections) return [];

    return assessment.sections.map(section => {
      const problemIds = (section.problems || []).map(p => p.id || p);
      const mcqIds = (section.mcqs || []).map(m => m.id || m);
      const allQuestionIds = [...problemIds, ...mcqIds];

      let sectionTotal = 0;

      allQuestionIds.forEach(questionId => {
        const question = assessment.questions[questionId];
        if (question) {
          sectionTotal += question.points || 0;
        }
      });

      const sectionScore = section.score || 0;
      const cutoff = section.cutoff || 0;

      let status = null;
      if (cutoff > 0) {
        status = sectionScore >= cutoff ? 'pass' : 'fail';
      }

      return {
        title: section.title,
        score: sectionScore,
        total: sectionTotal,
        cutoff: cutoff,
        status: status,
      };
    });
  };

  const totalScore = () => {
    if (!hasSections) return assessment.score || 0;

    const sectionScores = getSectionScores();
    let totalScore = 0;

    sectionScores.forEach(section => {
      totalScore += section.score || 0;
    });

    return totalScore;
  };

  // Calculate total possible score since API doesn't provide it directly
  const calculateTotalPossibleScore = () => {
    if (assessment.total_score) {
      return assessment.total_score;
    }

    let total = 0;

    // Check if we have questions with points
    if (assessment.questions) {
      Object.values(assessment.questions).forEach(question => {
        total += question.points || 0;
      });
    }

    // If no points data, use cutoff as rough estimate or 100
    if (total === 0) {
      total = assessment.cutoff || 100;
    }

    return total;
  };

  const totalPossibleScore = calculateTotalPossibleScore();
  const percent = (totalScore() / totalPossibleScore) * 100;

  const overallCutoff = assessment.cutoff || 0;

  const getOverallStatus = () => {
    // Use evaluation_status if available, otherwise status
    if (assessment.evaluation_status) {
      return assessment.evaluation_status;
    }

    if (overallCutoff > 0) {
      return totalScore() >= overallCutoff ? 'PS' : 'FL';
    }

    return assessment.status;
  };

  const displayStatus = getOverallStatus();
  const sectionScores = getSectionScores();

  const formatStartTime = () => {
    if (!assessment.start_time) return 'N/A';

    const date = new Date(assessment.start_time);
    return date.toLocaleString(); // Formats to local date and time
  };

  const calculateTimeTaken = () => {
    if (!assessment.start_time || !assessment.completion_time) return '0:00';

    const start = new Date(assessment.start_time);
    const end = new Date(assessment.completion_time);
    const duration = end - start; // in milliseconds

    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
        }}
        data-test-id="assessment-stats"
        data-specific-id="candidate-assessment-summary"
      >
        <Row
          gutter={[0, 16]}
          style={{ paddingTop: '12px', justifyContent: 'flex-start', alignItems: 'flex-start' }}
          data-test-id="stats-row"
          data-specific-id="assessment-stats-row"
        >
          <Col
            flex="0 0 auto"
            style={{ marginRight: '24px' }}
            data-test-id="col-progress"
            data-specific-id="progress-col"
          >
            <div
              style={{ flexShrink: 0, position: 'relative', bottom: '10px' }}
              data-test-id="progress-container"
              data-specific-id="score-progress-container"
            >
              <Progress
                type="circle"
                percent={percent}
                strokeLinecap="round"
                strokeColor="#3B82F6"
                trailColor="#E5E7EB"
                strokeWidth={8}
                width={150}
                height={150}
                format={percent => (
                  <div
                    style={{ textAlign: 'center' }}
                    data-test-id="progress-label"
                    data-specific-id="score-display"
                  >
                    <div
                      style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        color: '#111827',
                        lineHeight: '1',
                      }}
                      data-test-id="score-fraction"
                      data-specific-id="score-fraction-text"
                    >
                      {totalScore()}/{totalPossibleScore}
                    </div>
                  </div>
                )}
                data-test-id="assessment-progress"
                data-specific-id="circular-progress-bar"
              />
            </div>
          </Col>

          <Divider
            type="vertical"
            style={{ height: 'auto', margin: '0 24px' }}
            data-test-id="stats-divider-1"
            data-specific-id="stats-vertical-divider"
          />

          <Col
            flex="1 1 auto"
            style={{ minWidth: '150px', textAlign: 'left' }}
            data-test-id="col-name"
            data-specific-id="candidate-name-col"
          >
            <div
              data-test-id="name-section"
              data-specific-id="candidate-name-section"
            >
              <div
                style={{
                  fontSize: '20px',
                  color: 'black',
                  fontWeight: '500',
                  marginBottom: '12px',
                  textAlign: 'left',
                }}
                data-test-id="name-label"
                data-specific-id="test-name-label"
              >
                {t('practice_tests.test_result.test_name')}
              </div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '400',
                  color: '#111827',
                  marginBottom: '16px',
                  textAlign: 'left',
                  marginLeft: '10px',
                }}
                data-test-id="name-value"
                data-specific-id="candidate-name-value"
              >
                {assessment.test_title}
              </div>
              <div
                style={{
                  fontSize: '20px',
                  color: 'black',
                  fontWeight: '500',
                  marginBottom: '12px',
                  textAlign: 'left',
                }}
                data-test-id="start-time-label"
                data-specific-id="start-time-label-text"
              >
                {t('practice_tests.test_result.taken_at')}
              </div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '400',
                  color: '#111827',
                  textAlign: 'left',
                  marginLeft: '10px',
                }}
                data-test-id="start-time-value"
                data-specific-id="start-time-value"
              >
                {formatStartTime()}
              </div>
            </div>
          </Col>

          <Divider
            type="vertical"
            style={{ height: 'auto', margin: '0 24px' }}
            data-test-id="stats-divider-2"
            data-specific-id="score-col-divider"
          />

          <Col
            flex="1 1 auto"
            style={{ minWidth: '150px', textAlign: 'left' }}
            data-test-id="col-score-time"
            data-specific-id="score-time-col"
          >
            <div
              style={{ marginBottom: '16px' }}
              data-test-id="total-score-section"
              data-specific-id="total-score-section"
            >
              <div
                style={{
                  fontSize: '20px',
                  color: 'black',
                  fontWeight: '500',
                  marginBottom: '12px',
                  textAlign: 'left',
                }}
                data-test-id="total-score-label"
                data-specific-id="total-score-label-text"
              >
                {t('practice_tests.test_result.total_score')}
              </div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '400',
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  marginLeft: '10px',
                }}
                data-test-id="total-score-value"
                data-specific-id="total-score-fraction"
              >
                <span
                  data-test-id="score-span"
                  data-specific-id="score-span-text"
                >
                  {totalScore()}/{totalPossibleScore}
                </span>
                {overallCutoff > 0 && displayStatus && (
                  <Tag
                    color={
                      displayStatus === 'PS'
                        ? 'success'
                        : displayStatus === 'FL'
                        ? 'error'
                        : 'warning'
                    }
                    data-test-id="overall-status-tag"
                    data-specific-id="candidate-status-tag"
                  >
                    {candidate_status_map[displayStatus]}
                  </Tag>
                )}
              </div>
            </div>
            <div
              data-test-id="time-taken-section"
              data-specific-id="time-taken-section"
            >
              <div
                style={{
                  fontSize: '16px',
                  color: 'black',
                  fontWeight: '500',
                  marginBottom: '12px',
                  textAlign: 'left',
                }}
                data-test-id="time-taken-label"
                data-specific-id="time-taken-label-text"
              >
                {t('practice_tests.test_result.time_taken')}
              </div>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: '400',
                  color: '#111827',
                  textAlign: 'left',
                  marginLeft: '10px',
                }}
                data-test-id="time-taken-value"
                data-specific-id="assessment-time-value"
              >
                {calculateTimeTaken()}
              </div>
            </div>
          </Col>

          {hasSections && (
            <>
              <Divider
                type="vertical"
                style={{ height: 'auto', margin: '0 24px' }}
                data-test-id="stats-divider-3"
                data-specific-id="sections-col-divider"
              />
              <Col
                flex="1 1 auto"
                style={{ minWidth: '150px' }}
                data-test-id="col-sections"
                data-specific-id="sections-col"
              >
                {sectionScores.map((section, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: index < sectionScores.length - 1 ? '16px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                    data-test-id={`section-${index}-container`}
                    data-specific-id={`section-score-container-${index}`}
                  >
                    <div
                      style={{
                        fontSize: '20px',
                        fontWeight: '500',
                        minWidth: '80px',
                        flexShrink: 0,
                      }}
                      data-test-id={`section-${index}-title`}
                      data-specific-id={`section-name-${index}`}
                    >
                      {section.title}
                    </div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: '400',
                        color: '#111827',
                        minWidth: '50px',
                        flexShrink: 0,
                      }}
                      data-test-id={`section-${index}-score`}
                      data-specific-id={`section-score-${index}`}
                    >
                      {section.score}/{section.total}
                    </div>
                    {section.status && (
                      <Tag
                        color={section.status === 'pass' ? 'success' : 'error'}
                        style={{ flexShrink: 0 }}
                        data-test-id={`section-${index}-status-tag`}
                        data-specific-id={`section-status-${index}`}
                      >
                        {section.status === 'pass' ? 'Pass' : 'Fail'}
                      </Tag>
                    )}
                  </div>
                ))}
              </Col>
            </>
          )}
        </Row>
      </div>
    </>
  );
};

export default PracticeTestStats;
